'use client';

import { useEffect, useState, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import {
  QuizProgress,
  QuizButton,
  QuizSingleSelect,
  QuizMultiSelect,
  QuizTextInput,
  QuizInterstitial,
} from '@/components/quiz';
import { quizQuestions, getTotalQuestions } from '@/lib/quizQuestions';
import { Loader2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

const STORAGE_KEY = 'datehack_quiz_state';

// Generate a unique session ID
const generateSessionId = () => uuidv4().substring(0, 12);

export default function QuizPage() {
  const router = useRouter();
  const [currentStep, setCurrentStep] = useState(1);
  const [responses, setResponses] = useState<Record<string, string | string[]>>({});
  const [sessionId, setSessionId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const saveInProgressRef = useRef(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const totalSteps = getTotalQuestions();
  const currentQuestion = quizQuestions[currentStep - 1];

  // Load state from localStorage on mount OR generate new session ID
  useEffect(() => {
    const savedState = localStorage.getItem(STORAGE_KEY);
    if (savedState) {
      try {
        const { step, responses: savedResponses, sessionId: savedSessionId } = JSON.parse(savedState);
        setCurrentStep(step || 1);
        setResponses(savedResponses || {});
        setSessionId(savedSessionId || generateSessionId());
      } catch (error) {
        console.error('Error loading quiz state:', error);
        setSessionId(generateSessionId());
      }
    } else {
      // No saved state, generate new session ID
      setSessionId(generateSessionId());
    }
    setIsInitialized(true);
  }, []);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          step: currentStep,
          responses,
          sessionId,
        })
      );
    }
  }, [currentStep, responses, sessionId, isInitialized]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Save responses to API with debouncing and concurrency protection
  const saveResponsesToAPI = useCallback(
    async (isCompleted = false, email?: string, immediate = false) => {
      // Don't save if no sessionId yet or save already in progress
      if (!sessionId || (saveInProgressRef.current && !immediate)) {
        console.log('Skipping save - no session ID or save in progress');
        return;
      }

      // Clear any pending debounced save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }

      // If not immediate, debounce the save
      if (!immediate && !isCompleted) {
        saveTimeoutRef.current = setTimeout(() => {
          saveResponsesToAPI(isCompleted, email, true);
        }, 1000); // Wait 1 second before saving
        return;
      }

      // Prevent concurrent saves
      if (saveInProgressRef.current) {
        console.log('Save already in progress, skipping');
        return;
      }

      try {
        saveInProgressRef.current = true;
        console.log(`Saving quiz session ${sessionId}...`);

        const response = await fetch('/api/quiz/save-responses', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId,
            responses,
            email,
            completed: isCompleted,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to save responses');
        }

        const data = await response.json();
        console.log('Quiz session saved successfully:', data);
        return data;
      } catch (error) {
        console.error('Error saving responses:', error);
        throw error;
      } finally {
        saveInProgressRef.current = false;
      }
    },
    [sessionId, responses]
  );

  const handleSingleSelect = (value: string) => {
    setResponses({ ...responses, [currentQuestion.id]: value });
  };

  const handleMultiSelect = (value: string) => {
    const currentValues = (responses[currentQuestion.id] as string[]) || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value];
    setResponses({ ...responses, [currentQuestion.id]: newValues });
  };

  const handleTextInput = (value: string) => {
    setResponses({ ...responses, [currentQuestion.id]: value });
  };

  const canProceed = () => {
    if (currentQuestion.type === 'interstitial') return true;
    if (!currentQuestion.required) return true;

    const answer = responses[currentQuestion.id];
    if (!answer) return false;

    if (Array.isArray(answer)) {
      return answer.length > 0;
    }

    if (currentQuestion.type === 'text-input' && currentQuestion.id === 'q20_email') {
      // Basic email validation
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(answer as string);
    }

    return answer.toString().trim().length > 0;
  };

  const handleNext = async () => {
    // Save responses periodically (immediate save to avoid race conditions)
    if (currentStep % 3 === 0) {
      await saveResponsesToAPI(false, undefined, true);
    }

    if (currentStep === totalSteps) {
      // Final step - save with email and mark as completed
      setLoading(true);
      try {
        const email = responses.q20_email as string;
        await saveResponsesToAPI(true, email, true);

        // Clear localStorage
        localStorage.removeItem(STORAGE_KEY);

        // Redirect to results page
        router.push(`/quiz/results?sessionId=${sessionId}`);
      } catch (error) {
        console.error('Failed to save quiz:', error);
        setLoading(false);
        alert('Failed to submit quiz. Please try again.');
      }
    } else {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="animate-spin text-blue-500" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <QuizProgress
        currentStep={currentStep}
        totalSteps={totalSteps}
        onBack={handleBack}
        showBackButton={currentStep > 1}
      />

      <div className="py-8">
        {currentQuestion.type === 'single-select' && (
          <QuizSingleSelect
            question={currentQuestion.question!}
            instruction={currentQuestion.instruction}
            options={currentQuestion.options!}
            selectedValue={responses[currentQuestion.id] as string}
            onSelect={handleSingleSelect}
          />
        )}

        {currentQuestion.type === 'multi-select' && (
          <QuizMultiSelect
            question={currentQuestion.question!}
            instruction={currentQuestion.instruction}
            options={currentQuestion.options!}
            selectedValues={(responses[currentQuestion.id] as string[]) || []}
            onToggle={handleMultiSelect}
          />
        )}

        {currentQuestion.type === 'text-input' && (
          <QuizTextInput
            question={currentQuestion.question!}
            instruction={currentQuestion.instruction}
            value={(responses[currentQuestion.id] as string) || ''}
            onChange={handleTextInput}
            placeholder={currentQuestion.placeholder}
            type={currentQuestion.id === 'q20_email' ? 'email' : 'text'}
          />
        )}

        {currentQuestion.type === 'interstitial' && (
          <QuizInterstitial
            title={currentQuestion.title!}
            content={currentQuestion.content!}
            type={
              currentQuestion.id === 'q2_social_proof' || currentQuestion.id === 'q12_success_stories'
                ? 'social-proof'
                : currentQuestion.id === 'q19_expert'
                ? 'expert'
                : 'education'
            }
          />
        )}
      </div>

      <div className="max-w-2xl mx-auto px-4 pb-8">
        <QuizButton
          onClick={handleNext}
          disabled={!canProceed()}
          loading={loading}
        >
          {currentStep === totalSteps ? 'See My Results' : 'Next'}
        </QuizButton>
      </div>
    </div>
  );
}
